import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardHeader, CardBody, Form, Row, Col, Label, Button } from 'reactstrap'
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { fetchHolidayList } from '../../actions/holidayAction'
import { fetchBookingDays } from '../../actions/bookingAction'
import { fetchUnavailableVehicleDays } from '../../actions/vehicleAction'
import { createBooking } from '../../actions/bookingAction'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import '../../styles/booking-form.css'
import RewardPoints from "./RewardPoints";

const getUnavailbeDays = (unAvailableDays) => {
    return unAvailableDays.map( unAvailableDay => new Date(unAvailableDay))
}

const getHolidayDates = (holidays) => {
    return holidays.map(holiday => new Date(holiday.date));
};

const getBusyDays = (busydaysRaw) => {
    const dates = [];
    for (const start in busydaysRaw) {
        const startDate = new Date(start);
        const endDate = new Date(busydaysRaw[start]);
      
        while (startDate <= endDate) {
            dates.push(new Date(startDate));
            startDate.setDate(startDate.getDate() + 1);
        }
    }
    return dates;
};

const isHoliday = (date, holidays) => {
    return holidays.some(holiday => 
        date.getFullYear() === holiday.getFullYear() &&
        date.getMonth() === holiday.getMonth() &&
        date.getDate() === holiday.getDate()
    );
};

const isUnavailable = (date, unavailabledays) => {
    return unavailabledays.some(unavailableday => 
        date.getFullYear() === unavailableday.getFullYear() &&
        date.getMonth() === unavailableday.getMonth() &&
        date.getDate() === unavailableday.getDate()
    );
};

const isBusy = (date, busydays) => {
    return busydays.some(busyday => 
        date.getFullYear() === busyday.getFullYear() &&
        date.getMonth() === busyday.getMonth() &&
        date.getDate() === busyday.getDate()
    );
};

const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};

const filterDate = (date, holidays, unavailabledays, busydays) => {
    return !isHoliday(date, holidays) && !isUnavailable(date, unavailabledays) && !isBusy(date, busydays) && !isWeekend(date);
};

const calculateDaysDifference = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end date
    return diffDays;
};

const BookingForm = ({ vehicle = {} }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { data: holidays } = useSelector(state => state.holiday)
    const { unavailableVehicleDays: unavailabledays } = useSelector(state => state.vehicle)
    const { busyVehicleDays: busydays } = useSelector(state => state.vehicle)
    const rewardPointsState = useSelector((state) => state.reward || {});
    const authMember = useSelector(state => state.auth.authMember)

    const [vehicleId, setVehicleId] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [price, setPrice] = useState('')
    const [discountPrice, setDiscountPrice] = useState(0)
    const [discountPercent, setDiscountPercent] = useState(0)
    const [totalPrice, setTotalPrice] = useState('')
    const [collectionTime, setCollectionTime] = useState('3pm')
    const [returnTime, setReturnTime] = useState('3pm')
    const [createInvoice,  setCreateInvoice] = useState(false)
    const [errors, setErrors] = useState({})

    const BOOKING_STATUS_PENDING = 1
    const BOOKING_SHORT_TERM = 1

    useEffect(() => {
        dispatch(fetchHolidayList())
    }, [dispatch])

    useEffect(() => {
        if (vehicle.id) {
            dispatch(fetchBookingDays(vehicle.id))
            dispatch(fetchUnavailableVehicleDays(vehicle.id))
        }
    }, [dispatch,vehicle])

    useEffect(() => {
        if (vehicle) {
            setVehicleId(vehicle.id)
        }
    }, [vehicle])

    useEffect(() => {
        const startDate = new Date();
        const formattedStartDate = startDate.toISOString().split('T')[0]; 
        setStartDate(formattedStartDate);

        const endDate = new Date();
        endDate.setDate(startDate.getDate() + 1);
        const formattedEndDate = endDate.toISOString().split('T')[0];
        setEndDate(formattedEndDate);
    }, [])

    useEffect(() => {
        setPrice(vehicle.price || '');
        setTotalPrice(vehicle.price || '');
    }, [vehicle]);

    useEffect(() => {
        if (!startDate || !endDate || !price) return;
    
        const { redemption } = rewardPointsState || {};
        const discountPrice = redemption?.discount_amount || 0;
    
        const days = calculateDaysDifference(startDate, endDate);
        if (days <= 0) {
            setTotalPrice(0);
            return;
        }
    
        let totalPrice = (days - 1) * price;
    
        if (discountPrice > 0) {
            const discountPercent = (discountPrice / totalPrice) * 100;
            setDiscountPrice(discountPrice)
            setDiscountPercent(discountPercent)
            totalPrice -= discountPrice;
        }
    
        setTotalPrice(totalPrice);
    }, [startDate, endDate, price, rewardPointsState]);
    

    const handleStartDateChange = (date) => {
        const newStartDate = date.toISOString().split('T')[0];
        setStartDate(newStartDate);
    
        const minEndDate = new Date(date);
        minEndDate.setDate(minEndDate.getDate() + 1);
        const minEndDateString = minEndDate.toISOString().split('T')[0];
    
        if (endDate < minEndDateString) {
            setEndDate(minEndDateString);
        }
    }

    const handleEndDateChange = (date) => {
        const newEndDate = date.toISOString().split('T')[0];
        setEndDate(newEndDate);
    
        const minStartDate = new Date(date);
        minStartDate.setDate(minStartDate.getDate() - 1);
        const minStartDateString = minStartDate.toISOString().split('T')[0];
    
        if (startDate > minStartDateString) {
            setStartDate(minStartDateString);
        }
    }

    const validate = (data) => {
        let errors = {}
        if (!data.vehicleId) errors.vehicleId = 'Vehicle is required'
        if (!data.memberId) errors.memberId = 'Member is required'
        if (!data.startDate) errors.startDate = 'Start Date is required'
        if (!data.endDate) errors.endDate = 'End Date is required'
        if (!data.collectionTime) errors.collectionTime = 'Collection Time is required'
        if (!data.returnTime) errors.returnTime = 'Return Time is required'
        if (!data.bookingStatus) errors.bookingStatus = 'Booking Status is required'
        if (!data.bookingTerm) errors.bookingTerm = 'Booking Term is required'
        return errors
    }

    const handleBooking = async (e) => {
        e.preventDefault()       
        if (!authMember) {
            navigate('/register')
        } else {
            try {
                let formData = {
                    vehicleId,
                    'memberId': authMember.id,
                    startDate,
                    endDate,
                    collectionTime,
                    returnTime,
                    'bookingStatus': BOOKING_STATUS_PENDING,
                    'bookingTerm': BOOKING_SHORT_TERM,
                    createInvoice,
                    'discountAmount': discountPrice,
                    'discountPercent': discountPercent,
                    'redemptionId': rewardPointsState?.redemption?.id || ''
                }
                const validationErrors = validate(formData)
                setErrors(validationErrors)
                if (Object.keys(validationErrors).length === 0) {
                    const result = await dispatch(createBooking(formData))
                    navigate('/account/booking')
                }
            } catch (error) {
                if (error.code == 400) {
                    let errors = {};
                    for (let key in error.messages) {
                        if (error.messages.hasOwnProperty(key)) {
                            if (Array.isArray(error.messages[key])) {
                                error.messages[key].forEach(msg => {
                                    if (key == 'vehicle_id') errors.vehicleId = msg
                                    if (key == 'member_id') errors.memberId = msg
                                    if (key == 'start_date') errors.startDate = msg
                                    if (key == 'end_date') errors.endDate = msg
                                    if (key == 'collection_time') errors.collectionTime = msg
                                    if (key == 'return_time') errors.returnTime = msg
                                    if (key == 'booking_status') errors.bookingStatus = msg
                                    if (key == 'booking_term') errors.bookingTerm = msg
                                    if (key == 'create_invoice') errors.createInvoice = msg
                                    setErrors(errors)
                                })
                            }
                        }
                    }
                    toast.error('Validation error.')
                } else {
                    toast.error('Server error.')
                }
            }
        }
    }

    return <>
        <Card>
            <CardHeader style={{ fontWeight:'700px', fontSize:'1.3rem', textAlign:'center'}}>Book the car</CardHeader>
            <CardBody>
                <Form>
                    <RewardPoints/>
                    <Row className="mb-2">
                        <Col lg="6" md="6" sm="12">
                            <Row className="mt-2">
                                <Col lg="6" md="6" sm="12" className="pt-2">
                                    <Label htmlFor='startDate' className="form-label" >Start Date</Label>
                                </Col>
                                <Col lg="6" md="6" sm="12">
                                <DatePicker
                                        selected={startDate}
                                        onChange={handleStartDateChange}
                                        className="form-control custom-date-picker"
                                        id="startDate"
                                        name="start_date"
                                        dateFormat="yyyy-MM-dd"
                                        filterDate={date => filterDate(date, getHolidayDates(holidays), getUnavailbeDays(unavailabledays), getBusyDays(busydays))}
                                    />
                                    {errors.startDate && <p className='validation__error-message mb-0'>{errors.startDate}</p>}
                                </Col>
                            </Row>
                        </Col>
                        <Col lg="6" md="6" sm="12">
                            <Row className="mt-2">
                                <Col lg="6" md="6" sm="12" className="pt-2">
                                    <Label htmlFor='endDate' className="form-label" >End Date</Label>
                                </Col>
                                <Col lg="6" md="6" sm="12">
                                    <DatePicker
                                        selected={endDate}
                                        onChange={handleEndDateChange}
                                        className="form-control custom-date-picker"
                                        id="endDate"
                                        name="end_date"
                                        dateFormat="yyyy-MM-dd"
                                        filterDate={date => filterDate(date, getHolidayDates(holidays), getUnavailbeDays(unavailabledays), getBusyDays(busydays))}
                                    />
                                    {errors.endDate && <p className='validation__error-message mb-0'>{errors.endDate}</p>}
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg="6" md="6" sm="12">
                            <Row className="mt-2">
                                <Col lg="6" md="6" sm="12" className="pt-2">
                                    <Label htmlFor='collectionTime' className="form-label" >Collection Time</Label>
                                </Col>
                                <Col lg="6" md="6" sm="12">
                                <select 
                                        name="collectionTime" 
                                        id="collectionTime" 
                                        className="form-control" 
                                        value={collectionTime}
                                        onChange = { data => setCollectionTime(data.target.value) }
                                    >
                                        <option value='3pm'>3pm</option>
                                        <option value='4pm'>4pm</option>
                                        <option value='5pm'>5pm</option>
                                    </select>
                                    {errors.collectionTime && <p className='validation__error-message mb-0'>{errors.collectionTime}</p>}
                                </Col>
                            </Row>
                        </Col>
                        <Col lg="6" md="6" sm="12">
                            <Row className="mt-2">
                                <Col lg="6" md="6" sm="12" className="pt-2">
                                    <Label htmlFor='returnTime' className="form-label" >Return Time</Label>
                                </Col>
                                <Col lg="6" md="6" sm="12">
                                <select 
                                        name="returnTime" 
                                        id="returnTime" 
                                        className="form-control" 
                                        value={returnTime}
                                        onChange= { data => setReturnTime(data.target.value) }
                                    >
                                        <option value='3pm'>3pm</option>
                                        <option value='4pm'>4pm</option>
                                        <option value='5pm'>5pm</option>
                                    </select>
                                    {errors.returnTime && <p className='validation__error-message mb-0'>{errors.returnTime}</p>}
                                </Col>
                            </Row>   
                        </Col>
                    </Row>
                    <Row className="mt-4 mb-4">
                        <Col lg="12" md="12" sm="12">
                            {rewardPointsState?.redemption?.discount_amount && (
                                <p className="mb-0">
                                    You saved  <b> 
                                        { discountPercent ? discountPercent.toFixed(2) : "0" }% 
                                        (S$ {discountPrice ? discountPrice.toFixed(2) : "0.00"})!
                                    </b> 
                                </p>
                            )}
                            <Label>
                                <span style={{ display: 'inline-block', paddingRight: '15px' }}> Your Subscriptions </span> 
                                <span style={{ paddingRight: '15px' }}>{ startDate } - { endDate }</span>
                                <span> <b>{ (Number(totalPrice) || 0).toFixed(2) } S$</b> </span>
                            </Label>
                        </Col>
                    </Row>
                    <Row className="justify-content-center align-items-center">
                        <Col lg="12" md="12" sm="12" className="d-flex justify-content-center">
                            <Button className="btn btn-success w-100" onClick={ handleBooking }>Booking</Button>
                        </Col>
                    </Row>
                </Form>
            </CardBody>
        </Card>
        <ToastContainer />
    </>

}

export default BookingForm